import { useState, useEffect } from 'react';
import { useFile, useSelection, useVisualizer } from '../../context/AppContext.jsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableNode from '../dnd/DraggableNode.jsx';
import DraggableWaypoint from '../dnd/DraggableWaypoint.jsx';

export default function WaypointListSection() {
  const { currentWaypoints, nodes, reorderWaypointsInNode, reorderNodes } = useFile();
  const { selectedWaypointIndex, multiSelectedIndices, toggleMultiSelect, setSelectedWaypointIndex } = useSelection();
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [activeNodeIndex, setActiveNodeIndex] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Auto-expand node when a waypoint is selected
  useEffect(() => {
    if (selectedWaypointIndex !== null && nodes.length > 0) {
      // Find which node contains this waypoint
      const nodeIndex = nodes.findIndex(node => node.indices.includes(selectedWaypointIndex));
      if (nodeIndex !== -1 && !expandedNodes.has(nodeIndex)) {
        // Expand the node
        setExpandedNodes(prev => {
          const newSet = new Set(prev);
          newSet.add(nodeIndex);
          return newSet;
        });
      }
    }
  }, [selectedWaypointIndex, nodes, expandedNodes]);

  if (!currentWaypoints || currentWaypoints.length === 0) return null;

  const toggleNode = (nodeIndex) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeIndex)) {
        newSet.delete(nodeIndex);
      } else {
        newSet.add(nodeIndex);
      }
      return newSet;
    });
  };

  const handleWaypointClick = (index, e) => {
    if (e.ctrlKey || e.metaKey) {
      toggleMultiSelect(index);
    } else {
      setSelectedWaypointIndex(index);
      const event = new CustomEvent('waypointSelected', { detail: { index } });
      document.dispatchEvent(event);
    }
  };

  const getProcessTypeColor = (processType) => {
    const colors = {
      'Start': '#00ff00',
      'End': '#ff0000',
      'Approach': '#ffff00',
      'Depart': '#ff8800'
    };
    return colors[processType] || '#00ff00';
  };

  // Handle drag end for nodes
  const handleNodeDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Find indices based on stable IDs
      const oldIndex = nodes.findIndex(node => {
        const stableId = node._stableId || `node-${node.feature}-${node.type}`;
        return stableId === active.id;
      });

      const newIndex = nodes.findIndex(node => {
        const stableId = node._stableId || `node-${node.feature}-${node.type}`;
        return stableId === over.id;
      });

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedNodes = arrayMove(nodes, oldIndex, newIndex);
        reorderNodes(reorderedNodes);
      }
    }
  };

  // Handle drag end for waypoints within a node
  const handleWaypointDragEnd = (event, nodeIndex) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const node = nodes[nodeIndex];

      // Find indices based on stable IDs
      const oldIndex = node.indices.findIndex(idx => {
        const wp = currentWaypoints[idx];
        const stableId = wp?._stableId || `waypoint-${idx}`;
        return stableId === active.id;
      });

      const newIndex = node.indices.findIndex(idx => {
        const wp = currentWaypoints[idx];
        const stableId = wp?._stableId || `waypoint-${idx}`;
        return stableId === over.id;
      });

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedIndices = arrayMove(node.indices, oldIndex, newIndex);
        reorderWaypointsInNode(nodeIndex, reorderedIndices);
      }
    }
  };

  return (
    <section className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Waypoints</h2>
        <button
          onClick={() => {
            const allExpanded = nodes.length > 0 && nodes.every((_, idx) => expandedNodes.has(idx));
            if (allExpanded) {
              setExpandedNodes(new Set());
            } else {
              setExpandedNodes(new Set(nodes.map((_, idx) => idx)));
            }
          }}
          className="bg-bg-tertiary text-text-primary px-3 py-1.5 rounded text-sm hover:bg-bg-hover transition-colors"
        >
          {nodes.length > 0 && nodes.every((_, idx) => expandedNodes.has(idx)) ? 'Collapse All' : 'Expand All'}
        </button>
      </div>
      <p className="text-text-secondary text-sm mb-2">Click to select, Ctrl+Click for multiple. Drag ⋮⋮ to reorder.</p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleNodeDragEnd}
      >
        <SortableContext
          items={nodes.map(node => node._stableId || `node-${node.feature}-${node.type}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {nodes.map((node, nodeIndex) => {
              const stableId = node._stableId || `node-${node.feature}-${node.type}`;
              return (
              <DraggableNode
                key={stableId}
                node={node}
                nodeIndex={nodeIndex}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleWaypointDragEnd(event, nodeIndex)}
                >
                  <SortableContext
                    items={node.indices.map(idx => {
                      const wp = currentWaypoints[idx];
                      return wp?._stableId || `waypoint-${idx}`;
                    })}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="pl-4 space-y-1">
                      {node.indices.map(index => {
                        const waypoint = currentWaypoints[index];
                        if (!waypoint) return null;

                        const isSelected = selectedWaypointIndex === index;
                        const isMultiSelected = multiSelectedIndices.has(index);
                        const stableId = waypoint._stableId || `waypoint-${index}`;

                        return (
                          <DraggableWaypoint
                            key={stableId}
                            waypoint={waypoint}
                            index={index}
                            stableId={stableId}
                            isSelected={isSelected}
                            isMultiSelected={isMultiSelected}
                            handleWaypointClick={handleWaypointClick}
                            getProcessTypeColor={getProcessTypeColor}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </DraggableNode>
            );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}
